/**
 * ============================================================================
 * [P3-01-E] TextCard - 개별 글 카드 컴포넌트
 *
 * 역할:
 * - 글 미리보기 표시 (100자 truncate)
 * - 메타 정보 표시 (날짜, 주제, 타입, 글자수)
 * - 액션 버튼 (즐겨찾기, 복사, 편집, 삭제)
 *
 * 최적화:
 * - memo로 props가 변경될 때만 리렌더링
 * - useCallback으로 핸들러 메모이제이션
 * ============================================================================
 */

import { useCallback, memo } from 'react'
import { formatRelativeTime } from '@/utils'
import { useAppStore } from '@/stores'
import { TextCardActions } from './TextCardActions'
import type { SavedText } from '@/types'
import styles from './Saved.module.css'

// ============================================================================
// Props 타입 정의
// ============================================================================
interface TextCardProps {
  text: SavedText
  onDelete: (id: string) => Promise<boolean>
  onSelect: (text: SavedText) => void
  onToggleFavorite: (id: string) => Promise<boolean>  // [P3-01] 즐겨찾기 토글
  onToggleUsed: (id: string) => Promise<boolean>     // 레퍼런스 사용 상태 토글
  isUsed?: boolean  // 사용 상태 배지 표시 (reference: 자동+수동 / edit: 수동 토글). undefined면 배지 숨김
}

// ============================================================================
// Constants
// ============================================================================
const PREVIEW_LENGTH = 300

// ============================================================================
// Component
// ============================================================================
export const TextCard = memo(function TextCard({
  text,
  onDelete,
  onSelect,
  onToggleFavorite,
  onToggleUsed,
  isUsed,
}: TextCardProps) {
  // ========================================================================
  // Store 액션
  // ========================================================================
  const showToast = useAppStore((state) => state.showToast)
  const loadSavedTextToEditor = useAppStore((state) => state.loadSavedTextToEditor)

  // ========================================================================
  // [P3-01] 즐겨찾기 토글 핸들러
  // ========================================================================
  const handleToggleFavorite = useCallback(() => {
    onToggleFavorite(text.id)
  }, [text.id, onToggleFavorite])

  // ========================================================================
  // 레퍼런스 사용 상태 토글 핸들러
  // ========================================================================
  const handleToggleUsed = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleUsed(text.id)
  }, [text.id, onToggleUsed])

  // ========================================================================
  // 클립보드 복사 핸들러
  // ========================================================================
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text.content)
      showToast('클립보드에 복사되었습니다.', 'success')
    } catch (err) {
      console.error('[TextCard] 복사 실패:', err)
      showToast('복사에 실패했습니다.', 'error')
    }
  }, [text.content, showToast])

  // ========================================================================
  // 편집 핸들러 (편집기로 불러오기 - 레거시 동작 복원)
  // ========================================================================
  const handleEdit = useCallback(() => {
    loadSavedTextToEditor(text)
    const label = text.type === 'reference' ? '레퍼런스' : '작성 글'
    showToast(`${label}을 편집 영역으로 불러왔습니다.`, 'success')
  }, [text, loadSavedTextToEditor, showToast])

  // ========================================================================
  // 삭제 핸들러
  // ========================================================================
  const handleDelete = useCallback(async () => {
    if (window.confirm('정말 삭제하시겠습니까? 휴지통으로 이동됩니다.')) {
      const result = await onDelete(text.id)
      if (!result) {
        alert('삭제 중 오류가 발생했습니다.')
      }
    }
  }, [text.id, onDelete])

  // ========================================================================
  // 카드 클릭 핸들러 (상세 모달 열기)
  // ========================================================================
  const handleCardClick = useCallback(() => {
    onSelect(text)
  }, [text, onSelect])

  // ========================================================================
  // 미리보기 텍스트 생성
  // ========================================================================
  const previewContent = text.content.length > PREVIEW_LENGTH
    ? `${text.content.slice(0, PREVIEW_LENGTH)}...`
    : text.content

  // ========================================================================
  // 글자수 계산 (charLimit이 있으면 사용, 없으면 content.length)
  // ========================================================================
  const charCount = text.charLimit || text.content.length

  // ========================================================================
  // 날짜 포맷팅
  // ========================================================================
  const formattedDate = formatRelativeTime(
    text.createdAt instanceof Date ? text.createdAt : new Date(text.createdAt)
  )

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div
      className={`${styles.textCard} ${text.isFavorite ? styles.favoriteCard : ''}`}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleCardClick()
        }
      }}
    >
      {/* 헤더: 제목 + 글자수 */}
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>
          {text.title || '제목 없음'}
        </h3>
        <span className={styles.charCount}>{charCount}자</span>
      </div>

      {/* 본문 미리보기 */}
      <div className={styles.cardContent}>
        <p>{previewContent}</p>
      </div>

      {/* 메타 정보: 날짜, 주제, 타입, 사용 상태 */}
      <div className={styles.cardMeta}>
        <span className={styles.date}>{formattedDate}</span>
        {text.topic && (
          <span className={styles.topic}>#{text.topic}</span>
        )}
        <span className={styles.textType} data-type={text.type}>
          {text.type === 'reference' ? '레퍼런스' : '작성'}
        </span>
        {isUsed !== undefined && (
          <button
            type="button"
            className={`${styles.usageBadge} ${isUsed ? styles.usageBadgeUsed : styles.usageBadgeUnused}`}
            onClick={handleToggleUsed}
            title={isUsed ? '클릭하여 "사용 안됨"으로 변경' : '클릭하여 "사용됨"으로 변경'}
          >
            {isUsed ? '✅ 사용됨' : '🆕 사용 안됨'}
          </button>
        )}
      </div>

      {/* 액션 버튼 */}
      <TextCardActions
        isFavorite={text.isFavorite}
        onToggleFavorite={handleToggleFavorite}
        onCopy={handleCopy}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </div>
  )
})

export default TextCard
